import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {animate, state, style, transition, trigger} from "@angular/animations";
import {Observable, Subject, takeUntil} from "rxjs";
import {BaseDashboard} from "../../models/base-dashboard";
import {Pbx} from "../../../pbx/models/pbx";
import {NavigationService} from "../../services/navigation.service";
import {CallSessionManager} from "../../services/call-session-manager.service";
import {CallEntityKind} from "../../models/webrtc/call/v2/call-entity";
import {LocalizerService} from "../../services/localizer.service";
import {MediaStreamStore} from "../../services/media-stream-store.service";
import {UnitsManager} from "../../../units/services/units-manager";
import {PbxManager} from "../../../pbx/services/pbx-manager";
import {Store} from "@ngrx/store";
import {selectCallById} from "../../../state/call/current-call.selector";
import {CallEntry} from "../../../state/call/current-call-state";
import {CurrentCallActions} from "../../../state/call/current-call.actions";

@Component({
    selector: 'app-incoming-call-card',
    templateUrl: './incoming-call-card.component.html',
    styleUrl: './incoming-call-card.component.scss',
    animations: [
        trigger('curtainOpen', [
            state('hide', style({
                clipPath: 'circle(0% at 50% 50%)',
                opacity: 0,
                transform: 'scale(0.98)'
            })),
            state('show', style({
                clipPath: 'circle(100% at 50% 50%)',
                opacity: 1,
                transform: 'scale(1)'
            })),
            transition('show => hide', [
                animate('500ms cubic-bezier(.2,.9,.2,1)')
            ]),
            transition('hide => show', [
                animate('400ms cubic-bezier(.2,.9,.2,1)')
            ])
        ])
    ]
})
export class IncomingCallCardComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {

    @Input() incomingCallId: string | null = null;
    @Input() isActiveCall: boolean = false;
    @Input() cardIndex: number = 0;
    @Output() destroy = new EventEmitter<any>();

    @ViewChild('videoElem') videoRef!: ElementRef<HTMLVideoElement>;

    private onDestroy$ = new Subject<void>();
    private callState: CallEntry | null = null;

    showPlaceholder: boolean = true;
    autoplayBlocked: boolean = false;
    minimized: boolean = false;

    destination: string | null = null;
    caller: string = '';

    retrievingCardData: boolean = true;

    // Drag state
    isDragging = false;
    private hasDragged = false;
    position = { x: 20, y: 20 };
    private dragOffset = { x: 0, y: 0 };

    private static readonly STACK_OFFSET = 30;
    private static readonly SECONDARY_STACK_HEIGHT = 290;

    constructor(private callSessionManager: CallSessionManager, private cdr: ChangeDetectorRef,
                private unitManager: UnitsManager, private pbxManager: PbxManager,
                private navigation: NavigationService, private localizer: LocalizerService,
                private mediaStreamStore: MediaStreamStore, private store: Store) { }

    private get callId(): string | null {
        return this.callState?.callId ?? null;
    }

    private get destinationId(): string | null {
        return this.callState?.destinationId ?? null;
    }

    private get destinationKind(): CallEntityKind | null {
        return this.callState?.destinationKind ?? null;
    }

    private get installationId(): string | null {
        return this.callState?.installationId ?? null;
    }

    private get sessionId(): string | null {
        const mainLeg = this.callState?.mainLeg;
        if (!mainLeg) return null;
        const session = mainLeg.sessions.find(s => s.status === 'answered') ?? mainLeg.sessions[0];
        return session?.sessionId ?? null;
    }

    /** True when the call is still incoming (not yet answered) */
    protected get isIncoming(): boolean {
        return this.callState?.mainLeg?.status === 'incoming';
    }

    /** True for non-active calls (displayed smaller on the right) */
    get isSecondary(): boolean {
        return !this.isActiveCall;
    }

    ngOnChanges(changes: SimpleChanges) {
        if ((changes['isActiveCall'] || changes['cardIndex']) && !this.hasDragged) {
            if (this.isActiveCall) {
                this.position = { x: 20, y: 20 };
            } else {
                // Secondary cards: right side, stacked vertically from top
                this.position = {
                    x: 20,
                    y: 20 + this.cardIndex * IncomingCallCardComponent.SECONDARY_STACK_HEIGHT
                };
            }
        }
    }

    ngOnInit() {
        // Subscribe to Redux call state for this specific incoming call.
        // Must be in ngOnInit (not ngAfterViewInit) so that callState is set
        // before Angular's first template check, avoiding NG0100.
        console.log('[IncomingCallCard] subscribing to selectCallById:', this.incomingCallId);
        this.store.select(selectCallById(this.incomingCallId)).pipe(
            takeUntil(this.onDestroy$)
        ).subscribe(state => {
            console.log('[IncomingCallCard] state update:', { callId: state?.callId, mainLeg: state?.mainLeg?.status, pipLeg: state?.pipLeg?.status, destinationId: state?.destinationId });
            const prevState = this.callState;
            this.callState = state;

            // Fetch info when state first appears
            if (!prevState?.callId && state?.callId) {
                this.getInfo();
            }

            // Call ended — destroy card
            if (prevState?.callId && state && !state.mainLeg && !state.pipLeg) {
                this.onCallEnd();
            }

            // Call removed from store — destroy card
            if (prevState?.callId && !state) {
                this.onCallEnd();
            }
        });
    }

    ngAfterViewInit() {
        // Subscribe to video stream for this specific call
        this.mediaStreamStore.selectCallVideoStream(this.incomingCallId).pipe(
            takeUntil(this.onDestroy$)
        ).subscribe(stream => {
            if (this.videoRef?.nativeElement) {
                this.videoRef.nativeElement.srcObject = stream;
                if (stream) {
                    this.videoRef.nativeElement.play().catch(() => {
                        this.autoplayBlocked = true;
                        this.cdr.detectChanges();
                    });
                }
            }
        });
    }

    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    minimizeCard() {
        if (!this.minimized) {
            this.minimized = true;
            this.cdr.detectChanges();
        }
    }

    restoreCard() {
        if (this.minimized) {
            this.minimized = false;
            this.cdr.detectChanges();
        }
    }

    viewCall(event: Event) {
        event.stopPropagation();

        if (!this.installationId || !this.destinationId || !this.destinationKind)
            return;

        if (this.isSecondary && this.callId) {
            // Secondary card: make it the active call, reactive state handles the swap
            this.store.dispatch(CurrentCallActions.setActiveCall({ callId: this.callId }));
        }

        this.navigateToDestination();

        if (this.isActiveCall) {
            this.destroy.emit();
        }
    }

    answerCall(event: Event) {
        event.stopPropagation();

        if (!this.installationId || !this.destinationId || !this.destinationKind)
            return;

        if (this.destinationKind !== CallEntityKind.Unit && this.destinationKind !== CallEntityKind.PBX) {
            console.error("Unsupported entity kind on incoming call answer:", this.destinationKind);
            return;
        }

        if (this.isSecondary && this.callId) {
            // Secondary card: make it the active call before answering
            this.store.dispatch(CurrentCallActions.setActiveCall({ callId: this.callId }));
        }

        // Answer the call if we have a session
        if (this.callId && this.sessionId) {
            this.callSessionManager.startDeviceTalkSession(this.callId, this.sessionId);
        }

        this.navigateToDestination();

        if (this.isActiveCall) {
            this.destroy.emit();
        }
    }

    private navigateToDestination(): void {
        if (this.destinationKind == CallEntityKind.Unit)
            this.navigation.navigateToUnitDashboard(this.installationId!, this.destinationId!);
        else if (this.destinationKind == CallEntityKind.PBX)
            this.navigation.navigateToPbxDashboard(this.installationId!, this.destinationId!);
    }

    rejectCall(event: Event) {
        event.stopPropagation();
        if (!this.callId)
            return;

        this.callSessionManager.closeMainLeg(this.callId);
        this.onCallEnd();
    }

    onLoad() {
        this.showPlaceholder = false;
        this.autoplayBlocked = false;
        this.videoRef.nativeElement.muted = true;
    }

    resumePlayback() {
        if (this.videoRef?.nativeElement) {
            this.videoRef.nativeElement.play().then(() => {
                this.autoplayBlocked = false;
                this.cdr.detectChanges();
            }).catch(() => {});
        }
        // Also resume audio on the call
        if (this.callId) {
            this.callSessionManager.resumeAudio(this.callId);
            this.mediaStreamStore.setAutoplayBlocked(false);
        }
    }

    // Drag handlers
    onDragStart(event: MouseEvent) {
        if (this.minimized) return;
        this.isDragging = true;
        if (this.isSecondary) {
            // Secondary cards use right positioning
            this.dragOffset = {
                x: (window.innerWidth - event.clientX) - this.position.x,
                y: event.clientY - this.position.y
            };
        } else {
            this.dragOffset = {
                x: event.clientX - this.position.x,
                y: event.clientY - this.position.y
            };
        }
        event.preventDefault();
    }

    @HostListener('window:mousemove', ['$event'])
    onDragMove(event: MouseEvent) {
        if (!this.isDragging) return;
        this.hasDragged = true;
        if (this.isSecondary) {
            this.position = {
                x: (window.innerWidth - event.clientX) - this.dragOffset.x,
                y: event.clientY - this.dragOffset.y
            };
        } else {
            this.position = {
                x: event.clientX - this.dragOffset.x,
                y: event.clientY - this.dragOffset.y
            };
        }
    }

    @HostListener('window:mouseup')
    onDragEnd() {
        this.isDragging = false;
    }

    private getInfo() {
        if (!this.installationId || !this.destinationId || !this.destinationKind) return;

        this.retrievingCardData = true;

        const data$: Observable<BaseDashboard> = this.destinationKind == CallEntityKind.Unit
            ? this.unitManager.getUnitDashboard(this.installationId, this.destinationId)
            : this.pbxManager.getPbxDashboard(this.installationId, this.destinationId);

        data$.subscribe(data => {
            this.destination = data.name;

            const callerEntity = this.callState?.mainLeg?.entity;

            if (callerEntity?.kind == CallEntityKind.PBX) {
                this.caller = this.localizer.getLocalizedString("General.Pbx");
            } else if (callerEntity?.kind == CallEntityKind.Unit && this.destinationKind == CallEntityKind.PBX) {
                const callingUnit = (data as Pbx).units?.find(u => u.id == callerEntity.id);
                this.caller = callingUnit?.name || "";
            } else if (callerEntity?.kind != CallEntityKind.Unit) {
                this.caller = data.devices?.find(device => device.id == callerEntity?.id)?.name || "";
            }

            this.retrievingCardData = false;
        });
    }

    private onCallEnd() {
        this.destroy.emit(null);
    }
}
